import { ComponentProps, useEffect, useRef, useState } from 'react';
import { hotjar } from 'react-hotjar';
import { Link, useLocation } from 'react-router-dom';
import {
	ActivityReports,
	CommunicationCenterIcon,
	DemandReportNavbar,
	KnowledgeBase,
	ListWhite,
	LogoNoText2,
	Menu,
	NotificationBell,
	PlaneWhite,
	Resquared,
	ScheduledEmail,
	SearchHome,
	XIcon
} from '@/resources/assets/images';
import ResetPasswordModal from '@/modules/user/components/ResetPasswordModal';
import TwoFactorAuthModal from '@/modules/user/components/TwoFactorAuthModal/TwoFactorAuthModal';
import { shallowEqual, useSelector } from 'react-redux';
import type { RootState } from '@/rootReducer';
import { cn } from '@/lib/classNames';

import { NotificationModal } from './src/components/NotificationModal';
import { ProfileModal } from './src/components/ProfileModal';
import { useMenuToggle } from './src/hooks/useMenuToggle';

export const NavBar = () => {
	const menuRef = useRef<HTMLDivElement>(null);

	const { isMenuOpening, isMenuOpen, handleMenuToggle } =
		useMenuToggle(menuRef);

	const { pathname } = useLocation();

	const [isNotificationModalOpen, setIsNotificationModalOpen] =
		useState(false);
	const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
	const [showPasswordReset, setShowPasswordReset] = useState(false);
	const [show2faModal, setShow2faModal] = useState(false);

	const [user] = useSelector(
		(state: RootState) => [state.user.getIn(['authorize', 'user']).toJS()],
		shallowEqual
	);

	const isUserOnboarding = user?.onboarding?.status === 'pending';

	const sequencesAvailable =
		user?.threadedEmail ||
		user?.re2HostedEmail ||
		(user.re2SharedDomain && user?.features?.sharedDomainVersion !== 'v2');

	useEffect(() => {
		if (user && user.email) {
			hotjar.initialize(2916226, 6);
			if (hotjar.initialized()) {
				hotjar.identify(user._id, {
					name: user.name,
					email: user.email,
					role: user.role
				});
			}
		}
	}, [user]);

	const handleToggleNotificationModal = () => {
		setIsNotificationModalOpen(state => !state);
	};

	const handleCloseNotificationModal = () => {
		setIsNotificationModalOpen(false);
	};

	const handleToggleProfileModal = () => {
		setIsProfileModalOpen(state => !state);
	};

	const handleCloseProfileModal = () => {
		setIsProfileModalOpen(false);
	};

	const handleOpenTFAModal = () => {
		setShow2faModal(true);
	};

	const handleOpenPasswordResetModal = () => {
		setShowPasswordReset(true);
	};

	return (
		<div
			data-is-onboarding={isUserOnboarding}
			className="sticky left-0 top-0 z-150 h-16 w-full shrink-0 data-[is-onboarding=true]:cursor-not-allowed lg:h-full lg:w-20"
		>
			<nav
				data-is-onboarding={isUserOnboarding}
				className={cn(
					'group flex h-full w-full items-center justify-start gap-4 bg-dm-black p-4 shadow-sm shadow-dm-white/30 transition-[width] duration-300 data-[is-onboarding=true]:pointer-events-none',
					'lg:flex-col lg:items-start lg:justify-start lg:gap-12 lg:p-7',
					'lg:hover:w-56'
				)}
			>
				<Link
					to="/search-businesses"
					className="order-2 lg:order-1 lg:w-full"
				>
					<span className="flex cursor-pointer items-center justify-start gap-2">
						<LogoNoText2 className="block size-7 shrink-0" />
						<Resquared className="h-6 w-auto translate-y-1 transition-opacity duration-300 lg:opacity-0 lg:group-hover:opacity-100" />
					</span>
				</Link>

				<div
					ref={menuRef}
					className={cn(
						'relative order-1 flex h-9 w-9 items-center justify-center',
						'lg:order-2 lg:h-full lg:w-full lg:items-start lg:justify-start'
					)}
				>
					<button
						type="button"
						className="lg:hidden lg:h-0 lg:w-0"
						onClick={handleMenuToggle}
					>
						{isMenuOpen ? (
							<XIcon className="size-6 fill-dm-white" />
						) : (
							<Menu className="size-6 fill-dm-white" />
						)}
					</button>

					<nav
						data-is-menu-open={isMenuOpen}
						data-is-menu-opening={isMenuOpening}
						className={cn(
							'absolute left-0 top-0 flex h-0 w-0 max-w-80 translate-y-9 flex-col gap-12 p-0 transition-all',
							'data-[is-menu-opening=true]:overflow-hidden',
							'data-[is-menu-open=true]:h-auto data-[is-menu-open=true]:w-auto data-[is-menu-open=true]:min-w-[70vw] data-[is-menu-open=true]:p-6',
							'lg:relative lg:h-full lg:w-full lg:translate-y-0 lg:justify-between',
							"before:absolute before:bottom-0 before:left-0 before:right-0 before:top-0 before:origin-top-left before:scale-0 before:rounded-xl before:bg-dm-black1/[.98] before:transition before:content-['']",
							'data-[is-menu-open=true]:before:scale-100 data-[is-menu-open=true]:before:rounded-md data-[is-menu-open=true]:before:border data-[is-menu-open=true]:before:border-dm-gray',
							'lg:before:content-none lg:data-[is-menu-open=true]:h-full lg:data-[is-menu-open=true]:w-full lg:data-[is-menu-open=true]:min-w-0 lg:data-[is-menu-open=true]:p-0' // reset mobile state on large screens
						)}
					>
						<section
							data-is-menu-open={isMenuOpen}
							data-has-modal-open={isNotificationModalOpen}
							className={cn(
								'z-0 hidden h-full w-full flex-col gap-4',
								'data-[is-menu-open=true]:flex',
								'data-[has-modal-open=true]:z-10',
								'lg:flex lg:gap-6'
							)}
						>
							<Link to="/search-businesses" className="lg:w-full">
								<NavbarItemWrapper
									onClick={handleMenuToggle}
									isSelected={pathname.startsWith(
										'/search-businesses'
									)}
								>
									<NavbarItemIconWrapper>
										<SearchHome className="transition-colors group-hover/navitem:[&_path]:fill-orange-brighter group-data-[is-onboarding=true]:[&_path]:fill-dm-grey group-data-[is-selected=true]/navitem:[&_path]:fill-orange-brighter" />
									</NavbarItemIconWrapper>

									<NavbarItemTextWrapper>
										Search
									</NavbarItemTextWrapper>
								</NavbarItemWrapper>
							</Link>

							<Link to="/lists" className="lg:w-full">
								<NavbarItemWrapper
									onClick={handleMenuToggle}
									isSelected={pathname.startsWith('/lists')}
								>
									<NavbarItemIconWrapper>
										<ListWhite className="size-6 transition-colors group-hover/navitem:[&_path]:stroke-orange-brighter group-data-[is-onboarding=true]:[&_path]:stroke-dm-grey group-data-[is-selected=true]/navitem:[&_path]:stroke-orange-brighter" />
									</NavbarItemIconWrapper>

									<NavbarItemTextWrapper>
										Lists
									</NavbarItemTextWrapper>
								</NavbarItemWrapper>
							</Link>

							{sequencesAvailable && (
								<Link to="/sequences" className="lg:w-full">
									<NavbarItemWrapper
										onClick={handleMenuToggle}
										isSelected={pathname.startsWith(
											'/sequences'
										)}
									>
										<NavbarItemIconWrapper>
											<PlaneWhite className="transition-colors group-hover/navitem:[&_path]:fill-orange-brighter group-data-[is-onboarding=true]:[&_path]:fill-dm-grey group-data-[is-selected=true]/navitem:[&_path]:!fill-orange-brighter" />
										</NavbarItemIconWrapper>

										<NavbarItemTextWrapper>
											Sequences
										</NavbarItemTextWrapper>
									</NavbarItemWrapper>
								</Link>
							)}

							<Link
								to="/communication-center"
								className="lg:w-full"
							>
								<NavbarItemWrapper
									onClick={handleMenuToggle}
									isSelected={pathname.startsWith(
										'/communication-center'
									)}
								>
									<NavbarItemIconWrapper>
										<CommunicationCenterIcon className="size-5 transition-colors group-hover/navitem:[&_path]:fill-orange-brighter group-data-[is-onboarding=true]:[&_path]:fill-dm-grey group-data-[is-selected=true]/navitem:[&_path]:fill-orange-brighter" />
									</NavbarItemIconWrapper>

									<NavbarItemTextWrapper>
										Com. Center
									</NavbarItemTextWrapper>
								</NavbarItemWrapper>
							</Link>

							<Link to="/scheduled-emails" className="lg:w-full">
								<NavbarItemWrapper
									onClick={handleMenuToggle}
									isSelected={pathname.startsWith(
										'/scheduled-emails'
									)}
								>
									<NavbarItemIconWrapper>
										<ScheduledEmail className="transition-colors group-hover/navitem:[&_path]:stroke-orange-brighter group-data-[is-onboarding=true]:[&_path]:stroke-dm-grey group-data-[is-selected=true]/navitem:[&_path]:stroke-orange-brighter" />
									</NavbarItemIconWrapper>

									<NavbarItemTextWrapper>
										Scheduled Emails
									</NavbarItemTextWrapper>
								</NavbarItemWrapper>
							</Link>

							<Link to="/activity-reports" className="lg:w-full">
								<NavbarItemWrapper
									onClick={handleMenuToggle}
									isSelected={pathname.startsWith(
										'/activity-reports'
									)}
								>
									<NavbarItemIconWrapper>
										<ActivityReports className="transition-colors group-hover/navitem:[&_path]:fill-orange-brighter group-data-[is-onboarding=true]:[&_path]:fill-dm-grey group-data-[is-selected=true]/navitem:[&_path]:fill-orange-brighter" />
									</NavbarItemIconWrapper>

									<NavbarItemTextWrapper>
										Activity Reports
									</NavbarItemTextWrapper>
								</NavbarItemWrapper>
							</Link>

							<Link to="/demand-reports" className="lg:w-full">
								<NavbarItemWrapper
									onClick={handleMenuToggle}
									isSelected={pathname.startsWith(
										'/demand-reports'
									)}
								>
									<NavbarItemIconWrapper>
										<DemandReportNavbar className="transition-colors group-hover/navitem:[&_path]:fill-orange-brighter group-data-[is-onboarding=true]:[&_path]:fill-dm-grey group-data-[is-selected=true]/navitem:[&_path]:fill-orange-brighter" />
									</NavbarItemIconWrapper>

									<NavbarItemTextWrapper>
										Demand Reports
									</NavbarItemTextWrapper>
								</NavbarItemWrapper>
							</Link>

							<a
								href="https://learn.re2.ai/"
								target="_blank"
								rel="noopener noreferrer"
								className="lg:w-full"
							>
								<NavbarItemWrapper onClick={handleMenuToggle}>
									<NavbarItemIconWrapper>
										<KnowledgeBase className="transition-colors group-hover/navitem:[&_path]:fill-orange-brighter group-data-[is-onboarding=true]:[&_path]:fill-dm-grey" />
									</NavbarItemIconWrapper>

									<NavbarItemTextWrapper>
										Knowledge Base
									</NavbarItemTextWrapper>
								</NavbarItemWrapper>
							</a>

							<NotificationModal
								user={user}
								isOpen={isNotificationModalOpen}
								onClose={handleCloseNotificationModal}
							>
								<NavbarItemWrapper
									isSelected={isNotificationModalOpen}
									onClick={handleToggleNotificationModal}
								>
									<NavbarItemIconWrapper>
										<NotificationBell className="size-6 transition-colors group-hover/navitem:[&_path]:stroke-orange-brighter group-data-[is-onboarding=true]:[&_path]:stroke-dm-grey group-data-[is-selected=true]/navitem:[&_path]:stroke-orange-brighter" />
									</NavbarItemIconWrapper>

									<NavbarItemTextWrapper>
										Notifications
									</NavbarItemTextWrapper>
								</NavbarItemWrapper>
							</NotificationModal>
						</section>

						<section
							data-is-menu-open={isMenuOpen}
							data-has-modal-open={isProfileModalOpen}
							className={cn(
								'z-0 hidden w-full flex-col gap-5',
								'data-[is-menu-open=true]:flex',
								'data-[has-modal-open=true]:z-10',
								'lg:flex lg:gap-7'
							)}
						>
							<ProfileModal
								user={user}
								isOpen={isProfileModalOpen}
								onOpenTFAModal={handleOpenTFAModal}
								onClose={handleCloseProfileModal}
								onOpenPasswordResetModal={
									handleOpenPasswordResetModal
								}
							>
								<NavbarItemWrapper
									isSelected={isProfileModalOpen}
									onClick={handleToggleProfileModal}
								>
									<NavbarItemIconWrapper>
										<span className="flex h-full w-full items-center justify-center rounded-full bg-orange-opaque text-sm text-dm-white">
											{user.email?.[0].toUpperCase()}
										</span>
									</NavbarItemIconWrapper>

									<NavbarItemTextWrapper>
										Profile
									</NavbarItemTextWrapper>
								</NavbarItemWrapper>
							</ProfileModal>
						</section>
					</nav>
				</div>

				{show2faModal && (
					<TwoFactorAuthModal setShow2faModal={setShow2faModal} />
				)}

				{showPasswordReset && (
					<ResetPasswordModal
						onClose={() => setShowPasswordReset(false)}
						show={showPasswordReset}
					/>
				)}
			</nav>
		</div>
	);
};

type NavbarItemWrapperProps = Omit<ComponentProps<'button'>, 'className'> & {
	isSelected?: boolean;
};

const NavbarItemWrapper = ({
	isSelected = false,
	...props
}: NavbarItemWrapperProps) => (
	<button
		type="button"
		data-is-selected={isSelected}
		className={cn(
			'group/navitem flex w-full cursor-pointer items-center justify-start gap-2 fill-current'
		)}
		{...props}
	/>
);

const NavbarItemIconWrapper = (
	props: Omit<ComponentProps<'span'>, 'className'>
) => (
	<span
		className="flex size-6 shrink-0 items-center justify-center rounded-full"
		{...props}
	/>
);

const NavbarItemTextWrapper = (
	props: Omit<ComponentProps<'p'>, 'className'>
) => (
	<p
		className={cn(
			'block overflow-hidden whitespace-nowrap text-sm text-dm-white transition-[width] duration-300',
			'group-data-[is-selected=true]/navitem:text-orange-brighter',
			'group-hover/navitem:text-orange-brighter',
			'lg:w-0 lg:text-base lg:group-hover:w-auto'
		)}
		{...props}
	/>
);
